
import { formatInTimeZone } from 'date-fns-tz';
import tzlookup from 'tz-lookup';

/**
 * Clock times round to the nearest minute, the way Stellarium shows rise and
 * set times; formatting alone would drop the seconds (ERI-2396).
 */
export function toNearestMinute(date: Date): Date {
  return new Date(Math.round(date.getTime() / 60000) * 60000);
}

export function formatTime(date: Date | null, timezone?: string): string {
  if (!date || isNaN(date.getTime())) {
    return '--:--';
  }
  
  // If timezone is provided, use it; otherwise fall back to local time
  if (timezone) {
    return formatTimeInTimezone(date, timezone);
  }
  
  return toNearestMinute(date).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
}

export function formatTimeInTimezone(date: Date | null, timezone: string): string {
  if (!date || isNaN(date.getTime())) {
    return '--:--';
  }
  
  try {
    return formatInTimeZone(toNearestMinute(date), timezone, 'h:mm a');
  } catch (error) {
    console.error('Error formatting time in timezone:', error);
    return formatTime(date);
  }
}

export function getTimezoneFromCoordinates(latitude: number, longitude: number): string {
  try {
    return tzlookup(latitude, longitude);
  } catch {
    // Fallback for coordinates outside the lookup database
    const offsetHours = Math.round(longitude / 15);
    const sign = offsetHours <= 0 ? '+' : '-';
    return `Etc/GMT${sign}${Math.abs(offsetHours)}`;
  }
}

export function getTimezone(latitude: number, longitude: number): string {
  try {
    const timezone = getTimezoneFromCoordinates(latitude, longitude);
    
    // Get the display name
    const tempDate = new Date();
    const timeZone = Intl.DateTimeFormat('en', {
      timeZone: timezone,
      timeZoneName: 'short'
    }).formatToParts(tempDate).find(part => part.type === 'timeZoneName')?.value;
    
    return timeZone || timezone;
  } catch {
    return 'Local Time';
  }
}

export function getLocationParts(locationName: string) {
  const parts = locationName.split(',').map(part => part.trim());
  if (parts.length >= 3) {
    // Format: "City, State, Country"
    return {
      city: parts[0],
      stateProvince: parts[1],
      country: parts[2]
    };
  } else if (parts.length === 2) {
    // Format: "City, Country" or "City, State"
    return {
      city: parts[0],
      stateProvince: parts[1],
      country: ''
    };
  }
  return {
    city: locationName,
    stateProvince: '',
    country: ''
  };
}
